/**
 * Event date field — Event-Date Guarantee (rewritten 6 Aug 2026).
 *
 * Replaces the old JS-injected per-product "Date Required" line-item property
 * with ONE cart attribute: "Event date", stored in UK format (DD/MM/YYYY).
 *
 * - The same value is shared by the product page, cart page and cart drawer
 *   (inputs marked [data-event-date-input], rendered by
 *   snippets/event-date-guarantee.liquid).
 * - Past dates are rejected with an accessible error message.
 * - Changes persist immediately via /cart/update.js, and also submit with the
 *   cart form as a fallback via [data-event-date-hidden].
 * - The field is optional; nothing is ever forced.
 */
(function () {
  'use strict';

  var ATTR = 'Event date';
  var saved = null; // UK-format string once known ('' = none set)

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function todayISO() {
    var d = new Date();
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  function ukToISO(uk) {
    var m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((uk || '').trim());
    return m ? m[3] + '-' + pad(+m[2]) + '-' + pad(+m[1]) : '';
  }

  function isoToUK(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    return m ? m[3] + '/' + m[2] + '/' + m[1] : '';
  }

  function errorEl(input) {
    var id = input.getAttribute('data-error-target');
    return id ? document.getElementById(id) : null;
  }

  function setError(input, show) {
    var err = errorEl(input);
    if (!err) return;
    err.hidden = !show;
    if (show) {
      input.setAttribute('aria-invalid', 'true');
      var describedBy = input.getAttribute('aria-describedby') || '';
      if (describedBy.indexOf(err.id) === -1) {
        input.setAttribute('aria-describedby', (describedBy + ' ' + err.id).trim());
      }
    } else {
      input.removeAttribute('aria-invalid');
    }
  }

  function syncAll(uk) {
    var iso = ukToISO(uk);
    document.querySelectorAll('[data-event-date-input]').forEach(function (el) {
      if (el === document.activeElement) return; // never fight the user mid-entry
      if (el.value !== iso) el.value = iso;
      setError(el, false);
    });
    document.querySelectorAll('[data-event-date-hidden]').forEach(function (el) {
      el.value = uk;
    });
    updateRecos(uk);
  }

  function persist(uk) {
    saved = uk;
    syncAll(uk);
    var payload = { attributes: {} };
    payload.attributes[ATTR] = uk;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).catch(function () {
      /* Non-fatal: the hidden field still submits the value with the cart form. */
    });
  }

  function onChange(input) {
    var v = input.value;
    if (v && v < todayISO()) {
      setError(input, true);
      input.value = saved ? ukToISO(saved) : '';
      updateRecos(saved || '');
      return;
    }
    setError(input, false);
    var uk = v ? isoToUK(v) : '';
    if (uk === (saved || '')) return; // nothing changed (e.g. blur after no edit)
    persist(uk);
  }


  /* -------------------------------------------------------------------------
   * Delivery recommendation (added 9 Sep 2026; UK time + bank holidays 10 Sep 2026).
   * Turns the entered event date into an immediate buying recommendation,
   * rendered into [data-event-date-reco] elements placed under the field by
   * snippets/event-date-guarantee.liquid.
   *
   * WORKING-DAY MODEL (mirrors the published Event-Date Guarantee terms):
   * - Working days are Monday-Friday, EXCLUDING England & Wales bank holidays.
   *   Bank-holiday dates are official GOV.UK data: a verified list is baked in
   *   below and refreshed at runtime from https://www.gov.uk/bank-holidays.json
   *   (cached for 7 days). A bank holiday behaves exactly like a weekend: it
   *   cannot be the dispatch start day and never counts towards a window.
   * - All cutoff/calendar maths use UK wall-clock time (Europe/London, GMT/BST
   *   automatic) — the 2pm cutoff belongs to Zebra, not the customer's device.
   * - Effective start day: the UK "today" if it is a working day and UK time is
   *   before 14:00, otherwise the next working day.
   * - Free standard: allow the complete 5 working-days window.
   * - Express: allow the complete 2 working-days window.
   * - A method "gives enough time" only when the event date falls strictly
   *   AFTER the complete window (first delivery attempt before the event).
   * - SAFE FALLBACK: if a computed window reaches beyond the last date covered
   *   by verified bank-holiday data (baked or fetched), NO recommendation is
   *   shown at all — the module never guesses green.
   * This block reads state only; it never changes what is saved to the cart.
   * ---------------------------------------------------------------------- */
  var RECO_MSG = {
    ok: '✓ Free standard delivery gives enough time for your event date.',
    express: '→ Choose Express delivery at checkout for this event date.',
    close: '⚠ Your event date is close — please contact us before ordering.',
    bespoke: 'Bespoke order — we’ll confirm whether we can meet your event date after reviewing your brief.'
  };

  // England & Wales bank holidays, copied verbatim from the official GOV.UK
  // dataset (https://www.gov.uk/bank-holidays.json, division england-and-wales)
  // on 8 Sep 2026. Do not hand-edit: the runtime refresh below supersedes this.
  var BH_BAKED = [
    '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25', '2026-08-31', '2026-12-25', '2026-12-28',
    '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28',
    '2028-01-03', '2028-04-14', '2028-04-17', '2028-05-01', '2028-05-29', '2028-08-28', '2028-12-25', '2028-12-26'
  ];
  var bhSet = {};
  var bhCoverageEnd = '2028-12-31'; // GOV.UK data covers whole years
  BH_BAKED.forEach(function (d) { bhSet[d] = 1; });

  function applyBankHolidays(dates) {
    var maxYear = 0;
    dates.forEach(function (d) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
        bhSet[d] = 1;
        var y = +d.slice(0, 4);
        if (y > maxYear) maxYear = y;
      }
    });
    if (maxYear) {
      var end = maxYear + '-12-31';
      if (end > bhCoverageEnd) bhCoverageEnd = end;
    }
    updateRecos(saved || ''); // re-render in case classification changed
  }

  function refreshBankHolidays() {
    try {
      var cached = JSON.parse(localStorage.getItem('edgBankHolidays') || 'null');
      if (cached && cached.exp > Date.now() && cached.dates && cached.dates.length) {
        applyBankHolidays(cached.dates);
        return;
      }
    } catch (e) { /* fall through to fetch */ }
    fetch('https://www.gov.uk/bank-holidays.json', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var events = j && j['england-and-wales'] && j['england-and-wales'].events;
        if (!events || !events.length) return;
        var dates = events.map(function (e) { return e.date; });
        applyBankHolidays(dates);
        try {
          localStorage.setItem('edgBankHolidays', JSON.stringify({ dates: dates, exp: Date.now() + 7 * 864e5 }));
        } catch (e) { /* private mode etc. — baked data stands */ }
      })
      .catch(function () { /* offline / blocked — verified baked data stands */ });
  }

  function recoNow() {
    return window.__EDG_NOW ? new Date(window.__EDG_NOW) : new Date();
  }

  // UK wall-clock (Europe/London) for the current real moment, regardless of
  // the customer's device timezone. Returns null if the browser cannot do
  // timezone conversion (so the safe fallback hides the recommendation).
  var ukFormatter = null;
  try {
    ukFormatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
    ukFormatter.formatToParts(new Date()); // verify support up front
  } catch (e) {
    ukFormatter = null;
  }

  function ukNowParts() {
    if (!ukFormatter) return null;
    var parts = {};
    ukFormatter.formatToParts(recoNow()).forEach(function (p) { parts[p.type] = p.value; });
    return {
      iso: parts.year + '-' + parts.month + '-' + parts.day,
      hour: (+parts.hour) % 24 // hour12:false can yield "24" at midnight
    };
  }

  // Calendar-day helpers on ISO strings (timezone-free UTC arithmetic).
  function isoAddDays(iso, n) {
    var m = iso.split('-');
    var d = new Date(Date.UTC(+m[0], +m[1] - 1, +m[2] + n));
    return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
  }

  function isWorkingISO(iso) {
    var m = iso.split('-');
    var dow = new Date(Date.UTC(+m[0], +m[1] - 1, +m[2])).getUTCDay();
    return dow !== 0 && dow !== 6 && !bhSet[iso];
  }

  function nextWorkingISO(iso) {
    do { iso = isoAddDays(iso, 1); } while (!isWorkingISO(iso));
    return iso;
  }

  function addWorkingISO(iso, n) {
    for (var i = 0; i < n; i++) iso = nextWorkingISO(iso);
    return iso;
  }

  function classifyEventDate(iso) {
    if (!iso) return null;
    var uk = ukNowParts();
    if (!uk) return null; // cannot establish UK time — show nothing rather than guess
    if (iso < uk.iso) return null; // past dates are handled by the error message
    var start = uk.iso;
    if (!isWorkingISO(start) || uk.hour >= 14) start = nextWorkingISO(start);
    var latestExpress = addWorkingISO(start, 2);
    var latestStandard = addWorkingISO(start, 5);
    if (latestStandard > bhCoverageEnd) return null; // beyond verified bank-holiday data — never guess green
    if (iso > latestStandard) return 'ok';
    if (iso > latestExpress) return 'express';
    return 'close';
  }

  function updateRecos(uk) {
    var iso = ukToISO(uk);
    document.querySelectorAll('[data-event-date-reco]').forEach(function (el) {
      var state = null;
      if (iso) {
        state = el.hasAttribute('data-edg-bespoke') ? 'bespoke' : classifyEventDate(iso);
        if (state === 'bespoke') {
          var ukParts = ukNowParts();
          if (ukParts && iso < ukParts.iso) state = null; // past date: error message handles it
        }
      }
      var text = state ? RECO_MSG[state] : '';
      var cls = state ? 'event-date-guarantee__reco event-date-guarantee__reco--' + state : 'event-date-guarantee__reco';
      var hidden = !state;
      // Idempotent writes only: this runs from a MutationObserver, so writing
      // unchanged values would re-trigger the observer in a loop.
      if (el.textContent !== text) el.textContent = text;
      if (el.className !== cls) el.className = cls;
      if (el.hidden !== hidden) el.hidden = hidden;
    });
  }

  function initInputs() {
    document.querySelectorAll('[data-event-date-input]').forEach(function (input) {
      input.min = todayISO();
      if (input.dataset.edReady) return;
      input.dataset.edReady = '1';
      input.addEventListener('change', function () { onChange(input); });
      input.addEventListener('blur', function () { onChange(input); });
      input.addEventListener('input', function () {
        // A date input's value is only non-empty once a COMPLETE date is present,
        // so this warns immediately when a past date is typed, without flashing
        // errors mid-entry. The value is only reverted on commit (change/blur).
        var v = input.value;
        if (v && v < todayISO()) {
          setError(input, true);
          updateRecos('');
        } else {
          setError(input, false);
          updateRecos(v ? isoToUK(v) : (saved || ''));
        }
      });
      if (saved && !input.value) input.value = ukToISO(saved);
    });
  }

  function boot() {
    refreshBankHolidays();
    // Fast source of truth: the liquid-rendered hidden field (cart page/drawer).
    var hidden = document.querySelector('[data-event-date-hidden]');
    if (hidden && hidden.value) saved = hidden.value;
    initInputs();
    if (saved) syncAll(saved);

    // Authoritative source: the cart itself (covers the product page too).
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        saved = (cart && cart.attributes && cart.attributes[ATTR]) || '';
        initInputs();
        syncAll(saved);
      })
      .catch(function () { /* leave whatever we already have */ });

    // The cart drawer re-renders its contents after cart changes — re-bind then.
    var mo = new MutationObserver(function () {
      initInputs();
      if (saved !== null) syncAll(saved);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
